import { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useSucursal } from '../../utils/sucursalContext';

type TutorialVideo = {
  id: string;
  gimnasio_id: string;
  sucursal_id: string | null;
  titulo: string;
  descripcion: string | null;
  fuente_tipo: 'url' | 'storage';
  video_url: string | null;
  storage_bucket: string | null;
  storage_path: string | null;
  thumbnail_url: string | null;
  duracion_segundos: number | null;
  orden: number;
  activo: boolean;
  created_at: string;
  updated_at: string;
};

type TutorialForm = {
  titulo: string;
  descripcion: string;
  fuente_tipo: 'url' | 'storage';
  video_url: string;
  thumbnail_url: string;
  duracion_segundos: string;
  orden: string;
  activo: boolean;
};

const EMPTY_FORM: TutorialForm = {
  titulo: '',
  descripcion: '',
  fuente_tipo: 'url',
  video_url: '',
  thumbnail_url: '',
  duracion_segundos: '',
  orden: '0',
  activo: true,
};

const sanitizeFileName = (name: string) =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 120);

const openInNewTab = (url: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export default function TutorialesPage() {
  const { profile } = useAuth();
  const { sucursalActiva } = useSucursal();

  const gimnasioId = ((sucursalActiva as any)?.gimnasio_id || profile?.gimnasioId || null) as string | null;
  const sucursalId = (sucursalActiva?.id || null) as string | null;

  const [tutoriales, setTutoriales] = useState<TutorialVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<TutorialVideo | null>(null);
  const [form, setForm] = useState<TutorialForm>(EMPTY_FORM);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [openingPreviewId, setOpeningPreviewId] = useState<string | null>(null);

  const stats = useMemo(() => {
    const total = tutoriales.length;
    const activos = tutoriales.filter((t) => t.activo).length;
    const storage = tutoriales.filter((t) => t.fuente_tipo === 'storage').length;
    return { total, activos, storage };
  }, [tutoriales]);

  const fetchTutoriales = async () => {
    if (!gimnasioId) {
      setTutoriales([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase
        .from('tutorial_videos')
        .select('*')
        .eq('gimnasio_id', gimnasioId)
        .order('orden', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTutoriales((data as TutorialVideo[]) || []);
    } catch (err: any) {
      console.error('Error cargando tutoriales:', err);
      setError(err?.message || 'No se pudieron cargar los tutoriales.');
      setTutoriales([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTutoriales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gimnasioId]);

  const resetForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setVideoFile(null);
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (row: TutorialVideo) => {
    setEditing(row);
    setForm({
      titulo: row.titulo,
      descripcion: row.descripcion || '',
      fuente_tipo: row.fuente_tipo,
      video_url: row.video_url || '',
      thumbnail_url: row.thumbnail_url || '',
      duracion_segundos: row.duracion_segundos == null ? '' : String(row.duracion_segundos),
      orden: String(row.orden ?? 0),
      activo: !!row.activo,
    });
    setVideoFile(null);
    setShowModal(true);
  };

  const removeStorageObject = async (bucket: string | null, path: string | null) => {
    if (!bucket || !path) return;
    await supabase.storage.from(bucket).remove([path]);
  };

  const uploadVideoIfNeeded = async () => {
    if (form.fuente_tipo !== 'storage') return null;
    if (!videoFile) return null;
    if (!gimnasioId) throw new Error('No se pudo resolver el gimnasio activo.');

    const safeName = sanitizeFileName(videoFile.name || 'video.mp4');
    const path = `${gimnasioId}/${Date.now()}_${safeName}`;
    const { error: uploadError } = await supabase.storage
      .from('tutorial-videos')
      .upload(path, videoFile, {
        cacheControl: '3600',
        upsert: false,
        contentType: videoFile.type || 'video/mp4',
      });

    if (uploadError) throw uploadError;
    return { bucket: 'tutorial-videos', path };
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gimnasioId) {
      setError('No se pudo resolver el gimnasio activo.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setNotice(null);

      const titulo = form.titulo.trim();
      if (!titulo) throw new Error('El título es obligatorio.');

      let nextVideoUrl: string | null = null;
      let nextBucket: string | null = null;
      let nextPath: string | null = null;

      if (form.fuente_tipo === 'url') {
        const url = form.video_url.trim();
        if (!/^https?:\/\//i.test(url)) {
          throw new Error('La URL del video debe comenzar con http:// o https://');
        }
        nextVideoUrl = url;
      } else {
        const uploaded = await uploadVideoIfNeeded();
        if (uploaded) {
          nextBucket = uploaded.bucket;
          nextPath = uploaded.path;
        } else if (editing?.fuente_tipo === 'storage') {
          nextBucket = editing.storage_bucket;
          nextPath = editing.storage_path;
        } else {
          throw new Error('Debes seleccionar un archivo de video.');
        }
      }

      const payload = {
        gimnasio_id: gimnasioId,
        sucursal_id: sucursalId,
        titulo,
        descripcion: form.descripcion.trim() || null,
        fuente_tipo: form.fuente_tipo,
        video_url: form.fuente_tipo === 'url' ? nextVideoUrl : null,
        storage_bucket: form.fuente_tipo === 'storage' ? nextBucket : null,
        storage_path: form.fuente_tipo === 'storage' ? nextPath : null,
        thumbnail_url: form.thumbnail_url.trim() || null,
        duracion_segundos: form.duracion_segundos.trim() ? Number(form.duracion_segundos) : null,
        orden: Number(form.orden || '0') || 0,
        activo: !!form.activo,
        created_by_auth_id: profile?.id ?? null,
        updated_at: new Date().toISOString(),
      };

      if (editing) {
        const { error } = await supabase.from('tutorial_videos').update(payload).eq('id', editing.id);
        if (error) throw error;

        if (form.fuente_tipo !== 'storage' && editing.fuente_tipo === 'storage') {
          await removeStorageObject(editing.storage_bucket, editing.storage_path);
        } else if (
          form.fuente_tipo === 'storage' &&
          editing.fuente_tipo === 'storage' &&
          nextPath &&
          editing.storage_path &&
          nextPath !== editing.storage_path
        ) {
          await removeStorageObject(editing.storage_bucket, editing.storage_path);
        }
      } else {
        const { error } = await supabase.from('tutorial_videos').insert([payload]);
        if (error) throw error;
      }

      setShowModal(false);
      resetForm();
      setNotice('Tutorial guardado correctamente.');
      await fetchTutoriales();
    } catch (err: any) {
      console.error('Error guardando tutorial:', err);
      setError(err?.message || 'No se pudo guardar el tutorial.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: TutorialVideo) => {
    if (!confirm(`¿Eliminar "${row.titulo}"?`)) return;
    try {
      setError(null);
      const { error } = await supabase.from('tutorial_videos').delete().eq('id', row.id);
      if (error) throw error;
      if (row.fuente_tipo === 'storage') {
        await removeStorageObject(row.storage_bucket, row.storage_path);
      }
      await fetchTutoriales();
    } catch (err: any) {
      console.error('Error eliminando tutorial:', err);
      setError(err?.message || 'No se pudo eliminar el tutorial.');
    }
  };

  const toggleActivo = async (row: TutorialVideo) => {
    try {
      const { error } = await supabase
        .from('tutorial_videos')
        .update({ activo: !row.activo, updated_at: new Date().toISOString() })
        .eq('id', row.id);
      if (error) throw error;
      await fetchTutoriales();
    } catch (err: any) {
      setError(err?.message || 'No se pudo actualizar el estado.');
    }
  };

  const handlePreview = async (row: TutorialVideo) => {
    try {
      setOpeningPreviewId(row.id);
      if (row.fuente_tipo === 'url') {
        if (!row.video_url) throw new Error('El tutorial no tiene URL válida.');
        openInNewTab(row.video_url);
        return;
      }

      if (!row.storage_bucket || !row.storage_path) {
        throw new Error('El tutorial no tiene archivo asociado.');
      }

      const { data, error } = await supabase.storage
        .from(row.storage_bucket)
        .createSignedUrl(row.storage_path, 60 * 60);
      if (error) throw error;
      if (!data?.signedUrl) throw new Error('No se pudo obtener URL de vista previa.');
      openInNewTab(data.signedUrl);
    } catch (err: any) {
      setError(err?.message || 'No se pudo abrir el video.');
    } finally {
      setOpeningPreviewId(null);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tutoriales</h1>
            <p className="text-gray-600 mt-1">Sube o enlaza videos para que los usuarios los vean en la app móvil.</p>
          </div>
          <button
            onClick={openCreate}
            className="bg-teal-500 text-white px-6 py-3 rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-lg"></i>
            Nuevo Tutorial
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}
        {notice && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <i className="ri-checkbox-circle-line"></i>
            {notice}
          </div>
        )}
        {!gimnasioId && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg">
            Selecciona una sucursal/gimnasio para gestionar tutoriales.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Stat title="Total" value={stats.total} />
          <Stat title="Activos" value={stats.activos} color="text-green-600" />
          <Stat title="Subidos (archivo)" value={stats.storage} color="text-blue-600" />
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-500">Cargando tutoriales...</div>
        ) : tutoriales.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-gray-500">
            No hay tutoriales registrados para este gimnasio.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {tutoriales.map((row) => (
              <div key={row.id} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{row.titulo}</h3>
                    <div className="mt-1 text-sm text-gray-500">
                      {row.fuente_tipo === 'storage' ? 'Archivo subido' : 'URL externa'} · Orden {row.orden}
                    </div>
                    {row.descripcion ? <p className="text-sm text-gray-600 mt-2">{row.descripcion}</p> : null}
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      row.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {row.activo ? 'activo' : 'inactivo'}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-5 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handlePreview(row)}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer disabled:opacity-50"
                    disabled={openingPreviewId === row.id}
                  >
                    {openingPreviewId === row.id ? 'Abriendo...' : 'Ver video'}
                  </button>
                  <button
                    onClick={() => openEdit(row)}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => toggleActivo(row)}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer"
                  >
                    {row.activo ? 'Desactivar' : 'Activar'}
                  </button>
                  <button
                    onClick={() => handleDelete(row)}
                    className="px-4 py-2 text-sm rounded-lg border border-red-300 text-red-600 hover:bg-red-50 cursor-pointer"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">{editing ? 'Editar Tutorial' : 'Nuevo Tutorial'}</h2>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
                    <input
                      required
                      value={form.titulo}
                      onChange={(e) => setForm((prev) => ({ ...prev, titulo: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fuente *</label>
                    <select
                      value={form.fuente_tipo}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          fuente_tipo: e.target.value as 'url' | 'storage',
                        }))
                      }
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    >
                      <option value="url">URL externa</option>
                      <option value="storage">Archivo subido</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Orden</label>
                    <input
                      type="number"
                      value={form.orden}
                      onChange={(e) => setForm((prev) => ({ ...prev, orden: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                {form.fuente_tipo === 'url' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">URL del video *</label>
                    <input
                      required={form.fuente_tipo === 'url'}
                      value={form.video_url}
                      onChange={(e) => setForm((prev) => ({ ...prev, video_url: e.target.value }))}
                      placeholder="https://..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Archivo de video *</label>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm"
                    />
                    {!videoFile && editing?.fuente_tipo === 'storage' && editing.storage_path ? (
                      <p className="text-xs text-gray-500 mt-2">Archivo actual: {editing.storage_path}</p>
                    ) : null}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                  <textarea
                    rows={3}
                    value={form.descripcion}
                    onChange={(e) => setForm((prev) => ({ ...prev, descripcion: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Thumbnail URL (opcional)</label>
                    <input
                      value={form.thumbnail_url}
                      onChange={(e) => setForm((prev) => ({ ...prev, thumbnail_url: e.target.value }))}
                      placeholder="https://..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duración segundos (opcional)</label>
                    <input
                      type="number"
                      min="0"
                      value={form.duracion_segundos}
                      onChange={(e) => setForm((prev) => ({ ...prev, duracion_segundos: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.activo}
                    onChange={(e) => setForm((prev) => ({ ...prev, activo: e.target.checked }))}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded"
                  />
                  Activo (visible en app móvil)
                </label>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 cursor-pointer"
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-teal-500 text-white px-6 py-2.5 rounded-lg hover:bg-teal-600 disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {saving ? 'Guardando...' : 'Guardar tutorial'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Stat({ title, value, color = 'text-gray-900' }: { title: string; value: number; color?: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="text-sm text-gray-500">{title}</div>
      <div className={`text-3xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
